import {
  BoundingBox,
  Color,
  Engine,
  Keys,
  Scene,
  SceneActivationContext,
  vec,
  type DefaultLoader,
  type ExcaliburGraphicsContext,
} from "excalibur";
import { worldToHex } from "./grid/hex-grid";
import { COLONY } from "./colony/constants";
import { getActiveColonyConstants } from "./colony/colony-active-constants";
import { ColonyRuntime } from "./colony/colony-runtime";
import {
  applyColonySave,
  getColonySaveForSlot,
  syncMetaFromSaveData,
} from "./colony/colony-save";
import {
  DEFAULT_NEW_GAME_SETTINGS,
  gameSettingsFromSave,
} from "./colony/game-settings";
import { takePendingGameStart } from "./game-session";
import {
  defaultMetaProgress,
  writeMetaProgressToStorage,
} from "./colony/meta/meta-progress";
import { ActiveLevelComponent } from "./colony/ecs/components/colony-components";
import { setColonyBridge } from "./colony-bridge";
import { drawBeeJobLabels, drawBeeUnhappyThoughtBubbles } from "./render/bee-job-label";
import { drawHiveCellOverlays, drawHiveCells } from "./render/cell-renderer-actor";
import { terrainMapResource } from "./resources";
import { getFlowerDestinations } from "./tiled/flower-destinations";

/**
 * Main hive scene: camera pan vs tap placement, colony simulation, and UI snapshots.
 */
export class MyLevel extends Scene {
  readonly colony = new ColonyRuntime();
  private static readonly TILED_BACKGROUND_SCALE = 0.5;
  private lastPanScreen: { x: number; y: number } | null = null;
  private dragScreen = 0;
  private wasDown = false;
  /**
   * iOS often reports a bogus screen position on the first frame after touchstart,
   * then corrects it on the next frame. If we accumulate that delta into `dragScreen`,
   * a tap looks like a huge drag. Skip one frame of pan/drag accumulation after down.
   */
  private reseedPanAfterTouchStart = false;

  private addTiledBackground(): void {
    // Add the Tiled background behind gameplay actors/overlays and keep it centered on world 0,0.
    const mapW = terrainMapResource.map.width * terrainMapResource.map.tilewidth;
    const mapH = terrainMapResource.map.height * terrainMapResource.map.tileheight;
    const bgScale = MyLevel.TILED_BACKGROUND_SCALE;
    const scaledW = mapW * bgScale;
    const scaledH = mapH * bgScale;
    const mapPos = vec(-scaledW / 2, -scaledH / 2);

    terrainMapResource.addToScene(this, { pos: mapPos });
    for (const layer of terrainMapResource.getTileLayers()) {
      layer.tilemap.scale = vec(bgScale, bgScale);
    }

    // Prevent the player from panning beyond the edges of the tiled background.
    this.camera.strategy.limitCameraBounds(
      new BoundingBox(mapPos.x, mapPos.y, mapPos.x + scaledW, mapPos.y + scaledH),
    );

    this.colony.setFlowerDestinations(getFlowerDestinations(terrainMapResource));
  }

  override onInitialize(engine: Engine): void {
    this.backgroundColor = Color.fromHex("#1b2838");
    this.camera.pos = vec(0, 0);

    const start = takePendingGameStart();
    const loadSlotId = start?.loadSaveSlotId ?? null;
    const saveData = loadSlotId != null ? getColonySaveForSlot(loadSlotId) : null;
    const initMode = saveData ? "load" : "new";
    if (saveData) {
      syncMetaFromSaveData(saveData);
    } else {
      writeMetaProgressToStorage(defaultMetaProgress());
    }
    const gameSettings = saveData
      ? gameSettingsFromSave(saveData.gameSettings)
      : (start?.newGameOptions ?? DEFAULT_NEW_GAME_SETTINGS);
    this.colony.initialize(this, engine, { mode: initMode, gameSettings });
    if (saveData) {
      applyColonySave(this.colony, {
        data: saveData,
        seasonSystem: saveData.seasonSystem,
      });
    }

    // `applyColonySave()` clears all actors/entities, so add the background after it (and after
    // `initialize()` for new games) to ensure it always appears.
    this.addTiledBackground();

    setColonyBridge(this.colony);
  }

  override onPreLoad(loader: DefaultLoader): void {
    void loader;
  }

  override onActivate(_context: SceneActivationContext<unknown>): void {}

  override onDeactivate(_context: SceneActivationContext): void {}

  override onPreUpdate(engine: Engine, elapsed: number): void {
    void elapsed;
    this.handleDevSuccessionShortcuts(engine);
    const ctrl = this.colony.controllerEntity.get(ActiveLevelComponent)!;
    if (ctrl.transition !== "idle") {
      this.lastPanScreen = null;
      this.dragScreen = 0;
      this.wasDown = false;
      this.reseedPanAfterTouchStart = false;
      this.colony.updateHoverFromPointer();
      return;
    }
    const pointers = engine.input.pointers;
    const down = pointers.isDown(0);
    const primary = pointers.primary;
    const page = primary.lastPagePos;
    const screen = engine.screen.pageToScreenCoordinates(page);

    const hexSize = getActiveColonyConstants().hexSize;

    if (down) {
      if (!this.wasDown) {
        this.dragScreen = 0;
        this.lastPanScreen = { x: screen.x, y: screen.y };
        this.reseedPanAfterTouchStart = true;
      } else if (this.lastPanScreen) {
        if (this.reseedPanAfterTouchStart) {
          this.lastPanScreen = { x: screen.x, y: screen.y };
          this.reseedPanAfterTouchStart = false;
        } else {
          // Standard "drag to pan" mapping: convert finger delta in screen space
          // into world delta using camera zoom.
          const dx = screen.x - this.lastPanScreen.x;
          const dy = screen.y - this.lastPanScreen.y;
          const z = this.camera.zoom ?? 1;

          // Map-style panning: invert sign so the world moves with the finger
          // (drag direction matches finger motion).
          this.camera.pos = this.camera.pos.sub(vec(dx / z, dy / z));
          this.dragScreen += vec(dx, dy).size;
          this.lastPanScreen = { x: screen.x, y: screen.y };
        }
      } else {
        this.lastPanScreen = { x: screen.x, y: screen.y };
      }
    } else {
      if (this.wasDown && this.dragScreen >= COLONY.panTapThresholdPx) {
        this.colony.events.emit({ type: "CameraPanned" });
        if (this.colony.pendingCellTypeKey) {
          this.colony.dismissCellTypePicker();
        }
      }
      if (
        this.wasDown &&
        this.dragScreen < COLONY.panTapThresholdPx &&
        !pointers.isDragging(0)
      ) {
        const w = engine.screen.pageToWorldCoordinates(primary.lastPagePos);
        const h = worldToHex(w, hexSize);
        this.colony.handleTapIntent({
          q: h.q,
          r: h.r,
          level: this.colony.activeLevel,
        });
      }
      this.lastPanScreen = null;
      this.dragScreen = 0;
      this.reseedPanAfterTouchStart = false;
    }
    this.wasDown = down;
    this.colony.updateHoverFromPointer();
  }

  /**
   * Dev build only: Ctrl+Shift+1 optional succession, 2–4 mandatory reasons (starved / aged / other).
   */
  private handleDevSuccessionShortcuts(engine: Engine): void {
    if (!import.meta.env.DEV) {
      return;
    }
    const kb = engine.input.keyboard;
    const ctrl = kb.isHeld(Keys.ControlLeft) || kb.isHeld(Keys.ControlRight);
    const shift = kb.isHeld(Keys.ShiftLeft) || kb.isHeld(Keys.ShiftRight);
    if (!ctrl || !shift) {
      return;
    }
    if (!this.colony.lineageSystemEnabled) {
      return;
    }
    if (kb.wasPressed(Keys.Digit1)) {
      this.colony.debugOpenSuccessionOptional();
    } else if (kb.wasPressed(Keys.Digit2)) {
      this.colony.triggerMandatorySuccession("queenStarved");
    } else if (kb.wasPressed(Keys.Digit3)) {
      this.colony.triggerMandatorySuccession("queenAgedOut");
    } else if (kb.wasPressed(Keys.Digit4)) {
      this.colony.triggerMandatorySuccession("queenDiedOther");
    }
  }

  override onPostUpdate(engine: Engine, elapsed: number): void {
    void engine;
    this.colony.resourceDots.update(elapsed);
    this.colony.lastUiEmit += elapsed;
    if (this.colony.lastUiEmit >= COLONY.uiSnapshotMs) {
      this.colony.lastUiEmit = 0;
      this.colony.events.emit({
        type: "ColonySnapshot",
        snapshot: this.colony.getUiSnapshot(),
      });
    }
  }

  /**
   * Draw hive cell bodies and eligible-placement hex outlines before actors (scene order:
   * preDraw → world draw → postDraw).
   */
  override onPreDraw(ctx: ExcaliburGraphicsContext, _elapsed: number): void {
    ctx.save();
    ctx.resetTransform();
    this.camera.draw(ctx);
    drawHiveCells(ctx, this.colony);
    ctx.restore();
  }

  /** Hover hex ring above bees; job labels on top. */
  override onPostDraw(ctx: ExcaliburGraphicsContext, _elapsed: number): void {
    ctx.save();
    ctx.resetTransform();
    this.camera.draw(ctx);
    drawHiveCellOverlays(ctx, this.colony);
    this.colony.resourceDots.drawOverlays(ctx, this.colony);
    drawBeeJobLabels(ctx, this.colony);
    drawBeeUnhappyThoughtBubbles(ctx, this.colony);
    ctx.restore();
  }
}
